import { FC, ReactNode, useState } from 'react';
import { ReactComponent as TriangleIcon } from 'assets/icons/triangle.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { Controller } from 'react-hook-form';
import { Switch } from '@headlessui/react';
import ReactTooltip from 'react-tooltip';

interface ToggleSectionProps {
  control: any;
  name: string;
  title: string;
  tooltip?: ReactNode;
  // Overrides the default form update, e.g. to confirm the change first.
  onChange?: (value: boolean) => void;
}

const ToggleSection: FC<ToggleSectionProps> = ({
  control,
  name,
  title,
  tooltip,
  onChange,
}) => {
  const [isExapanded, setIsExapanded] = useState(true);

  const tooltipId = `${name}-info`;

  return (
    <div className="flex flex-col text-14 leading-[2.1rem]">
      <button
        className="flex items-center gap-x-0.8"
        onClick={() => setIsExapanded((isExapanded) => !isExapanded)}
      >
        <TriangleIcon
          className={`${
            isExapanded ? '' : '-rotate-90'
          } transition-transform w-1.6 h-1.6`}
        />
        <span className="font-bold text-16 leading-[2.2rem] text-darkest-grey">
          {title}
          {tooltip && (
            <span
              data-for={tooltipId}
              data-tip=""
              className="inline-block align-middle ml-0.8"
            >
              <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
            </span>
          )}
        </span>
      </button>
      {tooltip && (
        <ReactTooltip
          id={tooltipId}
          effect="solid"
          place="bottom"
          className="!bg-white !rounded-[0.8rem] !w-[21rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
        >
          <span className="text-11 text-darkest-grey flex items-center font-normal text-left">
            {tooltip}
          </span>
        </ReactTooltip>
      )}
      {isExapanded && (
        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            const { value } = field;
            return (
              <div className="space-y-2.1">
                <div className="flex gap-x-1.6 items-center mt-1">
                  <Switch
                    {...field}
                    checked={value}
                    className={`${
                      value ? 'bg-magenta' : 'bg-[#78788029]'
                    } relative inline-flex h-3.1 w-5.1 items-center rounded-full`}
                    onChange={onChange ?? field.onChange}
                  >
                    <span
                      className={`${
                        value ? 'translate-x-[2.3rem]' : 'translate-x-[2px]'
                      } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
                    />
                  </Switch>
                  <span className="uppercase">{value ? 'On' : 'Off'}</span>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default ToggleSection;
